import { agentCaller } from '../../../lib/ai/agentCaller';
import { processHtmlOutput, processJsonOutput } from '../../../components/agents/documentAi';

const buildPrompt = (settings, previousOutputs = []) => {
  let prompt = settings.userPrompt || '';
  let formatInstructions = '';

  // Add output format instructions
  if (settings.outputFormat === 'html') {
    formatInstructions = `
Output Rules:
1. Output only valid HTML content that can be placed inside a <body> tag
2. Do not include <html>, <head>, or <body> tags
3. Use semantic HTML elements appropriately
4. Ensure all tags are properly closed
5. Do not include any markdown or other formatting
6. Do not include any explanatory text before or after the HTML`;
  } else if (settings.outputFormat === 'json') {
    formatInstructions = `
Output Rules:
1. Output only valid JSON that can be parsed with JSON.parse()
2. ${settings.jsonExample ? 
      `Follow this exact structure:\n${settings.jsonExample}` : 
      'Output must be an array of objects with consistent structure'}
3. Use proper data types (string, number, boolean, etc.)
4. Do not include any explanatory text before or after the JSON
5. Do not use comments or trailing commas
6. Use double quotes for property names and string values`;
  }

  // Add selected upstream node outputs
  if (previousOutputs.length > 0) {
    console.log('Building prompt with outputs:', previousOutputs);
    previousOutputs.forEach(output => {
      console.log('Processing output:', output);
      if (!output || !output.data) {
        console.log('Skipping invalid output:', output);
        return;
      }

      if (output.type === 'getdocument') {
        console.log('Processing document output:', output.data);
        if (!output.data.name || !output.data.content) {
          console.warn('Invalid document data structure:', output.data);
          return;
        }
        prompt += `\n\nReview this document as part of your response:\nDocument: ${output.data.name}\nContent: ${output.data.content}\n`;
      } else if (output.type === 'getdata') {
        const dataStr = Array.isArray(output.data) 
          ? output.data.map(item => JSON.stringify(item, null, 2)).join('\n')
          : JSON.stringify(output.data, null, 2);
        prompt += `\n\nConsider this data in your response:\n${dataStr}\n`;
      } else if (output.type === 'agent') {
        prompt += `\n\nIncorporate this previous agent's response:\n${output.data}\n`;
      } else {
        prompt += `\n\nAdditional information:\n${JSON.stringify(output.data, null, 2)}\n`;
      }
    });
  }

  // Add format instructions at the end to emphasize their importance
  prompt += `\n\n${formatInstructions}`;

  console.log('Final prompt:', prompt);
  return prompt;
};

export async function executeNode({settings, allPreviousOutputs = {}, projectId, organizationId, userId}) {
  console.log('=== AGENT NODE EXECUTION START ===');
  console.log('Agent settings:', settings);
  console.log('All previous outputs:', allPreviousOutputs);
  console.log('Selected upstream nodes:', settings.selectedUpstreamNodes);

  try {
    // Get selected upstream node outputs
    const selectedOutputs = settings.selectedUpstreamNodes
      .map(nodeId => {
        console.log('Looking for output from node:', nodeId);
        const output = allPreviousOutputs[nodeId];
        console.log('Found output for node', nodeId, ':', output);

        // Basic validation
        if (!output) {
          console.warn(`No output found for node ${nodeId}`);
          return null;
        }

        // Data validation and logging
        if (!output.data) {
          console.warn(`Invalid or missing output data for node ${nodeId}`);
          return null;
        }

        // Log the structure of the output
        console.log('Output type:', output.type);
        console.log('Output data structure:', {
          hasData: !!output.data,
          dataType: typeof output.data,
          isArray: Array.isArray(output.data),
          keys: output.data ? Object.keys(output.data) : null
        });

        return output;
      })
      .filter(output => output !== null);

    console.log('Filtered selected outputs:', selectedOutputs);

    if (selectedOutputs.length === 0) {
      console.warn('No valid outputs found from selected upstream nodes');
      console.warn('Available outputs:', allPreviousOutputs);
    }

    // Build the prompt with format instructions
    const prompt = buildPrompt(settings, selectedOutputs);
    console.log('Built prompt:', prompt);

    // Add format-specific system prompt additions
    let systemPrompt = 'You only ever focus on answering the user prompt. Do not include any other information. ';
    
    if (settings.outputFormat === 'html') {
      systemPrompt += 'You are an HTML specialist that only outputs valid HTML content. ';
    } else if (settings.outputFormat === 'json') {
      systemPrompt += 'You are a data specialist that only outputs valid JSON structures. ';
    }

    // Call the agent
    const payload = {
      userPrompt: prompt,
      systemPrompt,
      agentName: settings.agentType || 'dave',
      projectId: projectId,
      organizationId: organizationId,
      currentUserId: userId
    };

    console.log('AGENT PAYLOAD', payload)

    const result = await agentCaller(payload);

    if (!result.success) {
      throw new Error(result.error || 'Failed to execute agent');
    }

    if (settings.outputFormat === 'html') {
      result.output = processHtmlOutput(result.output); 
    } else if (settings.outputFormat === 'json') {
      result.output = processJsonOutput(result.output);
    }

    return {
      success: true,
      output: result.output,
      type: 'agent',
      nodeId: settings.id
    };

  } catch (error) {
    console.error('Error executing agent node:', error);
    throw error;
  }
}
